/** A suggestion's review: conversation, proposed changes and shared decisions. */
import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, FileTextIcon, LightbulbIcon } from "lucide-react";

import {
  ComplianceOrganizationMissing,
  ComplianceUnavailable,
} from "../components/regulation/ComplianceUnavailable";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import {
  PolicyRecommendations,
  ProofLink,
} from "../components/regulation/policy/PolicyRecommendations";
import { SuggestionConversation } from "../components/regulation/policy/SuggestionConversation";
import {
  SuggestionReview,
  SuggestionChanges,
} from "../components/regulation/policy/SuggestionReview";
import {
  KIND_LABELS,
  SUGGESTION_STATUS_PILL,
} from "../components/regulation/policy/suggestionsView";
import { suggestionSourceLabel } from "../components/regulation/policy/SuggestionReview.logic";
import { suggestionDocumentLabel } from "../components/regulation/policy/SuggestionQueue.logic";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { useRecommendations } from "../state/governance";
import { policyDisplayName, usePolicies } from "../state/policies";
import {
  ReviewDetailLayout,
  ReviewDetails,
  ReviewDetailRow,
  ReviewDetailGroup,
  ReviewSummary,
} from "../components/regulation/ReviewDetail";
import { formatDate } from "../components/regulation/policy/PolicyGovernanceTabs";
import { ReviewPageHeader, ReviewComposerDock, REVIEW_BACK_TRANSFORM } from "../components/regulation/ReviewPageHeader";
import {
  findingSeverityTone,
  FINDING_SEVERITY_LABELS,
} from "../components/regulation/finding/findingView";
import { suggestionTldr } from "../components/regulation/tldr";
import { AnimatedTabs, AnimatedTabsContent } from "../components/ui/animated-tabs";
import { validateReviewSearch } from "../components/regulation/reviewPageSearch";
import { StartFromTemplateLink } from "../components/regulation/templates/StartFromTemplateLink";
import { cn } from "~/lib/utils";

function RegulationSuggestionRoute() {
  const { org: orgSegment, recommendationId } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { snapshot, unavailableReason, refresh } = useRecommendations(organizationId);
  const { snapshot: library } = usePolicies(organizationId);
  const suggestion = snapshot?.recommendations.find((item) => item.id === recommendationId) ?? null;
  const policy = library?.policies.find((item) => item.id === suggestion?.policyId);

  const search = Route.useSearch();
  const tab = search.tab ?? "overview";
  const navigate = Route.useNavigate();
  const setTab = (value: unknown) => {
    void navigate({
      search: validateReviewSearch({ ...search, tab: value }),
      replace: true,
      resetScroll: false,
    });
  };
  const [composerHost, setComposerHost] = useState<HTMLDivElement | null>(null);
  const backToPolicy = search.fromPolicy === suggestion?.policyId ? search.fromPolicy : undefined;
  const backLabel = backToPolicy
    ? `Back to ${policy?.documentKind === "procedure" ? "procedure" : "policy"}`
    : "All suggestions";
  const back = (compact = false) => (
    <Link
      {...(backToPolicy
        ? {
            to: "/$org/compliance/policies/$policyId" as const,
            params: { org: orgSegment, policyId: backToPolicy },
            hash: "suggestions",
          }
        : { to: "/$org/compliance/suggestions" as const, params: { org: orgSegment } })}
      aria-label={backLabel}
      title={backLabel}
      className={cn(
        "inline-flex w-fit cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground",
        compact && "size-8 shrink-0 justify-center rounded-md hover:bg-accent",
      )}
      style={
        compact
          ? {
              transform: REVIEW_BACK_TRANSFORM,
            }
          : undefined
      }
    >
      <ArrowLeftIcon aria-hidden className="size-4" />
      <span className={compact ? "sr-only" : undefined}>{backLabel}</span>
    </Link>
  );

  if (organizationId === null || unavailableReason !== null || suggestion === null) {
    return (
      <RegulationPageShell eyebrow={back()} title="Suggestion">
        {() => (
          <div className="mt-6">
            {organizationId === null && !isLoading ? (
              <ComplianceOrganizationMissing />
            ) : unavailableReason !== null ? (
              <ComplianceUnavailable reason={unavailableReason} onRetry={refresh} />
            ) : snapshot === null || isLoading ? (
              <ListSkeleton rows={2} />
            ) : (
              <div className="py-12 text-center">
                <LightbulbIcon aria-hidden className="mx-auto size-5 text-muted-foreground/60" />
                <p className="mt-3 text-sm font-medium">Suggestion not found</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  It is not in {org?.name ?? "your organization"}'s record.
                </p>
              </div>
            )}
          </div>
        )}
      </RegulationPageShell>
    );
  }
  const status = SUGGESTION_STATUS_PILL[suggestion.status];
  const documentName = policy
    ? policyDisplayName(policy)
    : suggestionDocumentLabel(suggestion).displayName;
  const tone = findingSeverityTone(suggestion.severity);
  return (
    <AnimatedTabs value={tab} onValueChange={setTab} className="min-h-0 flex-1 gap-0">
      <RegulationPageShell
        eyebrow={back()}
        title={suggestion.title}
        floatingFooter
        footer={<ReviewComposerDock tab={tab} hostRef={setComposerHost} />}
        stickyHeader={
          <ReviewPageHeader
            kind="suggestion"
            title={suggestion.title}
            description={suggestionTldr(suggestion)}
            id={suggestion.id}
            back={back(true)}
            status={
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                {suggestion.severity ? (
                  <span
                    className={cn(
                      "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold",
                      tone.badge,
                    )}
                  >
                    {FINDING_SEVERITY_LABELS[suggestion.severity] ?? suggestion.severity}
                  </span>
                ) : null}
                <span
                  className={cn(
                    "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium",
                    status.className,
                  )}
                >
                  {status.label}
                </span>
              </div>
            }
            actions={
              <PolicyRecommendations
                organizationId={organizationId}
                policyId={null}
                everything
                decisionOnly="header"
                filter={(item) => item.id === recommendationId}
              />
            }
          />
        }
      >
        {() => (
          <>
            <AnimatedTabsContent value="overview" keepMounted>
              <ReviewDetailLayout
                summary={
                  <ReviewSummary
                    key={`summary:${organizationId}:${recommendationId}`}
                    organizationId={organizationId}
                    kind="suggestion"
                    entityId={recommendationId}
                  />
                }
                details={
                  <ReviewDetails kind="suggestion" id={suggestion.id}>
                    <dl className="px-4 py-3">
                      <ReviewDetailRow label="Status">{status.label}</ReviewDetailRow>
                      <ReviewDetailRow label="Type">{KIND_LABELS[suggestion.kind]}</ReviewDetailRow>
                      {suggestion.severity ? (
                        <ReviewDetailRow label="Priority">
                          <span className="capitalize">{suggestion.severity}</span>
                        </ReviewDetailRow>
                      ) : null}
                      <ReviewDetailRow label="Raised by">
                        {suggestionSourceLabel(suggestion.source)}
                      </ReviewDetailRow>
                      <ReviewDetailRow label="Opened">
                        {formatDate(suggestion.createdAt)}
                      </ReviewDetailRow>
                    </dl>
                    <ReviewDetailGroup label="Document">
                      {suggestion.policyId ? (
                        <Link
                          to="/$org/compliance/policies/$policyId"
                          params={{ org: orgSegment, policyId: suggestion.policyId }}
                          className="group flex items-start gap-2 text-sm leading-5 hover:underline"
                        >
                          <FileTextIcon
                            aria-hidden
                            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                          />
                          <span className="min-w-0 break-words">{documentName}</span>
                        </Link>
                      ) : (
                        <p className="text-sm">{documentName || "Library"}</p>
                      )}
                    </ReviewDetailGroup>
                    {suggestion.decidedAt ? (
                      <dl className="px-4 py-3">
                        <ReviewDetailRow label="Reviewed">
                          {formatDate(suggestion.decidedAt)}
                        </ReviewDetailRow>
                        {suggestion.decisionProof ? (
                          <ReviewDetailRow label="Proof">
                            <ProofLink
                              organizationId={organizationId}
                              recommendation={suggestion}
                              separator={false}
                            />
                          </ReviewDetailRow>
                        ) : null}
                        {suggestion.decidedByEmail ? (
                          <ReviewDetailRow label="By">{suggestion.decidedByEmail}</ReviewDetailRow>
                        ) : null}
                      </dl>
                    ) : null}
                  </ReviewDetails>
                }
              >
                <SuggestionReview
                  key={`review:${organizationId}:${recommendationId}`}
                  suggestion={suggestion}
                  onViewChanges={() => setTab("changes")}
                  comments={
                    <SuggestionConversation
                      organizationId={organizationId}
                      recommendationId={recommendationId}
                      composerHost={composerHost}
                    />
                  }
                />
              </ReviewDetailLayout>
            </AnimatedTabsContent>
            <AnimatedTabsContent value="changes" className="pt-2 pb-10">
              <SuggestionChanges
                suggestion={suggestion}
                organizationId={organizationId}
                currentVersion={policy ? (policy.version ?? 1) : null}
                missingDocumentAction={
                  <StartFromTemplateLink forDocument={suggestion.title} label="Draft from a template" size="xs" />
                }
              />
            </AnimatedTabsContent>
          </>
        )}
      </RegulationPageShell>
    </AnimatedTabs>
  );
}

export const Route = createFileRoute("/$org/compliance/suggestions_/$recommendationId")({
  validateSearch: validateReviewSearch,
  component: RegulationSuggestionRoute,
});
